<!-- ViewApplicationDocumentModal.vue - Preview application documents (PDF, images) inline -->
<template>
  <BaseModal
    :is-visible="isVisible"
    :title="document ? document.file_name : 'Document Preview'"
    :subtitle="document ? `${getCategoryLabel(document.document_category)} • ${formatFileSize(document.file_size)}` : ''"
    :icon="FileText"
    size="4xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >
    <div v-if="document" class="space-y-4">
      <!-- Header Info Strip -->
      <div class="p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="p-2 rounded-xl bg-secondary/10 dark:bg-secondary-dark/20 text-secondary dark:text-secondary-dark-text flex-shrink-0">
            <FileText v-if="isPdf" class="w-5 h-5 text-red-500" />
            <ImageIcon v-else-if="isImage" class="w-5 h-5 text-emerald-500" />
            <FileText v-else class="w-5 h-5 text-blue-500" />
          </div>
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-secondary/10 text-secondary dark:text-secondary-dark-text border border-secondary/20 capitalize">
                {{ getCategoryLabel(document.document_category) }}
              </span>
              <span
                v-if="document.expiry_date"
                class="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
              >
                Expires: {{ document.expiry_date }}
              </span>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {{ formatFileSize(document.file_size) }} • Type: {{ document.file_type || 'Unknown' }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <BaseButton
            text="Download"
            variant="outline"
            size="sm"
            :icon="Download"
            @click="handleDownload"
          />
          <a
            v-if="previewBlobUrl"
            :href="previewBlobUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-secondary dark:text-secondary-dark-text hover:bg-secondary/10 transition"
          >
            <ExternalLink class="w-4 h-4" />
            <span>Open in Tab</span>
          </a>
        </div>
      </div>

      <!-- Preview Box -->
      <div class="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-100 dark:bg-gray-900 min-h-[420px] flex flex-col">
        <!-- Loading Indicator -->
        <div v-if="isLoading" class="flex-1 flex flex-col items-center justify-center py-20 space-y-3">
          <div class="w-8 h-8 border-3 border-secondary border-t-transparent rounded-full animate-spin"></div>
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Loading document preview...</p>
        </div>

        <!-- Load Error -->
        <div v-else-if="hasError" class="flex-1 flex flex-col items-center justify-center py-16 px-4 text-center space-y-3">
          <AlertCircle class="w-10 h-10 text-red-500" />
          <div>
            <h4 class="text-sm font-bold text-gray-800 dark:text-gray-200">Unable to load inline preview</h4>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Please download the file to view its full contents.</p>
          </div>
          <BaseButton
            text="Download Document"
            variant="primary"
            size="sm"
            :icon="Download"
            @click="handleDownload"
          />
        </div>

        <!-- PDF Preview Frame -->
        <div v-else-if="isPdf && previewBlobUrl" class="w-full h-[600px]">
          <iframe
            :src="previewBlobUrl"
            class="w-full h-full border-0"
            title="Application Document PDF Preview"
          />
        </div>

        <!-- Docx Preview Frame -->
        <div v-else-if="isDocx && (previewBlobUrl || previewBlob)" class="w-full">
          <DocxViewer
            :src="previewBlob || previewBlobUrl"
            :file-name="document.file_name"
            max-height="600px"
            :allow-download="false"
            @download="handleDownload"
          />
        </div>

        <!-- Image Preview Frame -->
        <div v-else-if="isImage && previewBlobUrl" class="flex-1 flex items-center justify-center p-6 bg-checkerboard">
          <img
            :src="previewBlobUrl"
            :alt="document.file_name"
            class="max-h-[550px] max-w-full rounded-xl shadow-lg object-contain"
          />
        </div>

        <!-- Non-previewable Document (Spreadsheets, archives, etc) -->
        <div v-else class="flex-1 flex flex-col items-center justify-center py-16 px-4 text-center space-y-3">
          <div class="w-14 h-14 rounded-2xl bg-gray-200 dark:bg-gray-800 text-gray-500 flex items-center justify-center">
            <FileText class="w-7 h-7" />
          </div>
          <div>
            <h4 class="text-sm font-bold text-gray-800 dark:text-gray-200">
              Inline preview not supported for this file format
            </h4>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Spreadsheets and archives can be downloaded directly to your device.
            </p>
          </div>
          <BaseButton
            text="Download File"
            variant="primary"
            size="sm"
            :icon="Download"
            @click="handleDownload"
          />
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <template #footer>
      <div class="flex items-center justify-between w-full">
        <BaseButton
          variant="outline"
          size="sm"
          text="Close"
          @click="closeModal"
        />
        <BaseButton
          v-if="document"
          variant="primary"
          size="sm"
          text="Download File"
          :icon="Download"
          @click="handleDownload"
        />
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { BaseModal, BaseButton } from '@/components/common'
import DocxViewer from '@/components/common/DocxViewer.vue'
import type { ApplicationDocument } from '@/types/application'
import { applicationService } from '@/services/applicationService'
import {
  FileText,
  ImageIcon,
  Download,
  ExternalLink,
  AlertCircle,
} from 'lucide-vue-next'

interface Props {
  isVisible: boolean
  document?: ApplicationDocument | null
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'close'): void
}>()

const isLoading = ref(false)
const hasError = ref(false)
const previewBlobUrl = ref<string | null>(null)
const previewBlob = ref<Blob | null>(null)

const isPdf = computed(() => {
  const name = props.document?.file_name?.toLowerCase() || ''
  const type = props.document?.file_type?.toLowerCase() || ''
  return name.endsWith('.pdf') || type.includes('pdf')
})

const isImage = computed(() => {
  const name = props.document?.file_name?.toLowerCase() || ''
  const type = props.document?.file_type?.toLowerCase() || ''
  return (
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png') ||
    name.endsWith('.gif') ||
    name.endsWith('.webp') ||
    type.startsWith('image/')
  )
})

const isDocx = computed(() => {
  const name = props.document?.file_name?.toLowerCase() || ''
  const type = props.document?.file_type?.toLowerCase() || ''
  return (
    name.endsWith('.docx') ||
    name.endsWith('.doc') ||
    type.includes('word') ||
    type.includes('officedocument')
  )
})

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

const getCategoryLabel = (category?: string) => {
  switch (category) {
    case 'background_check':
      return 'Background Check Form'
    case 'job_application':
      return 'Job Application Form'
    case 'tax_document':
      return 'Tax Documents'
    case 'certification':
      return 'Certification / License'
    case 'identification':
      return 'Identification / ID Proof'
    case 'resume':
      return 'Resume / CV'
    case 'paper_record':
      return 'Scanned Paper Record'
    case 'general':
    default:
      return category ? category.replace(/_/g, ' ') : 'General Document'
  }
}

const loadPreview = async () => {
  if (!props.document?.document_id) return
  if (!isPdf.value && !isImage.value && !isDocx.value) return

  isLoading.value = true
  hasError.value = false

  try {
    const blob = await applicationService.previewDocumentBlob(props.document.document_id)
    previewBlob.value = blob
    if (previewBlobUrl.value) {
      URL.revokeObjectURL(previewBlobUrl.value)
    }
    previewBlobUrl.value = URL.createObjectURL(blob)
  } catch (err) {
    console.error('Failed to load document preview blob:', err)
    hasError.value = true
  } finally {
    isLoading.value = false
  }
}

const cleanupBlobUrl = () => {
  if (previewBlobUrl.value) {
    URL.revokeObjectURL(previewBlobUrl.value)
    previewBlobUrl.value = null
  }
  previewBlob.value = null
}

watch(
  () => [props.isVisible, props.document?.document_id],
  ([visible, docId]) => {
    if (visible && docId) {
      loadPreview()
    } else {
      cleanupBlobUrl()
    }
  }
)

const closeModal = () => {
  cleanupBlobUrl()
  emit('close')
}

const handleDownload = async () => {
  if (!props.document?.document_id) return
  await applicationService.downloadDocument(props.document.document_id, props.document.file_name)
}
</script>

